import * as React from 'react';

import { useTranslation } from 'react-i18next';

import { SingleSelectWithPagination } from '@/modules/common/components/molecules/selectors/single-select/single-select-with-pagination';
import { AddOnboardingEntitiesModal } from '@/modules/onboarding/common/components/add-entities-modal/add-onboarding-entities-modal';
import {
  CustomFieldRenderProps,
  EntityField,
} from '@/modules/onboarding/common/components/add-entities-modal/types';

import { useClientSelectLoaders, useUpsertProjects } from '../../hooks';
import {
  CreateProjectsFormData,
  ProjectFields,
  ProjectsFormData,
  UpsertProjectsBulkRequest,
} from '../../types';
import { getProjectsSchema } from '../../validations/projects-schema';

const ARRAY_FIELD_NAME = 'projects';

const defaultProject = { [ProjectFields.Name]: '', [ProjectFields.ClientId]: null };

interface AddProjectsModalProps {
  onSuccess?: (addedCount: number) => void;
}

export function AddOnboardingProjectsModal({ onSuccess }: AddProjectsModalProps) {
  const { t } = useTranslation('common');
  const { loadItems, searchItems } = useClientSelectLoaders();

  const schema = getProjectsSchema();
  const { mutateAsync: addProjects } = useUpsertProjects({ onSuccess });

  const renderClientField = ({
    control,
    name,
    index,
  }: CustomFieldRenderProps<CreateProjectsFormData>) => (
    <SingleSelectWithPagination
      control={control}
      name={name}
      label={t('authenticated.onboarding.projects:addModal.client')}
      placeholder={t('authenticated.onboarding.projects:addModal.selectClient')}
      queryKey={['onboarding-clients', String(index)]}
      loadItems={loadItems}
      searchItems={searchItems}
      isClearSelectionEnabled={true}
    />
  );

  const projectFields: EntityField<CreateProjectsFormData>[] = [
    {
      name: ProjectFields.Name,
      label: `${t('authenticated.onboarding.projects:addModal.projectName')}*`,
      placeholder: t('authenticated.onboarding.projects:addModal.projectName'),
    },
    {
      name: ProjectFields.ClientId,
      label: t('authenticated.onboarding.projects:addModal.client'),
      placeholder: t('authenticated.onboarding.projects:addModal.selectClient'),
      renderField: renderClientField,
    },
  ];

  const transformData = (formData: ProjectsFormData): UpsertProjectsBulkRequest => {
    return {
      items: formData.projects.map(project => ({
        name: project.name,
        ...(project.clientId ? { clientId: parseInt(project.clientId, 10) } : {}),
      })),
    };
  };

  return (
    <AddOnboardingEntitiesModal<CreateProjectsFormData, UpsertProjectsBulkRequest>
      schema={schema}
      fields={projectFields}
      mutation={addProjects}
      transformData={transformData}
      defaultEntityValues={{
        projects: [defaultProject],
      }}
      arrayFieldName={ARRAY_FIELD_NAME}
      buttonLabel={t('authenticated.onboarding.projects:addModal.addProjects')}
      modalTitle={t('authenticated.onboarding.projects:addModal.addProjects')}
      addMoreLabel={t('authenticated.onboarding.projects:addModal.addMore')}
      submitLabel={t('authenticated.onboarding.projects:addModal.addProjects')}
    />
  );
}
